import { forwardRef, useState, useEffect, useContext, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { CircleMarker, DivIcon } from 'leaflet';
import { MarkerClusterGroup } from 'leaflet.markercluster';

import { noop } from 'utils';

import { MapContext } from './map-container';
import {
	MAP_CLUSTER_ICON_FACTORY,
	MAP_CLUSTER_MARKER_FACTORY,
	MAP_CLUSTER_DEFAULT_OPTIONS,
} from './options';

// import styles from './map-cluster.module.scss';

export const MapCluster = forwardRef(
	(
		{
			latLngs,
			children,
			options,
			iconFactory = MAP_CLUSTER_ICON_FACTORY,
			markerFactory = MAP_CLUSTER_MARKER_FACTORY,
			onInitialized = noop,
		},
		ref
	) => {
		const [cluster, setCluster] = useState(null);
		const map = useContext(MapContext);

		useImperativeHandle(ref, () => cluster, [cluster]);

		useEffect(() => {
			if (map === null) {
				return;
			}

			const cluster = new MarkerClusterGroup({
				...MAP_CLUSTER_DEFAULT_OPTIONS,
				...{
					iconCreateFunction: iconFactory,
				},
				...options,
			});
			setCluster(cluster);

			const markers = latLngs.map(markerFactory);
			cluster.addLayers(markers);
			map.addLayer(cluster);

			const teardown = onInitialized(map, cluster);

			return () => {
				if (typeof teardown === 'function') {
					teardown();
				}

				cluster.clearLayers();
				cluster.remove();
				setCluster(null);
			};
		}, [map, latLngs, options, iconFactory, markerFactory, onInitialized]);

		return children;
	}
);

MapCluster.propTypes = {
	latLngs: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
	options: PropTypes.shape({
		maxClusterRadius: PropTypes.number,
		disableClusteringAtZoom: PropTypes.number,
		animate: PropTypes.bool,
		animateAddingMarkers: PropTypes.bool,
		spiderfyOnMaxZoom: PropTypes.bool,
		zoomToBoundsOnClick: PropTypes.bool,
		showCoverageOnHover: PropTypes.bool,
	}),
	iconFactory: PropTypes.func,
	markerFactory: PropTypes.func,
	onInitialized: PropTypes.func,
};
